import React from 'react'
import Switch from '@material-ui/core/Switch'
// @src imports
import { noop } from 'src/helpers/appHelpers'

import { PlanPaymentInfo } from 'src/payments/containers/Payment'
import { AccountFragment, AccountInput } from 'src/graphql'
import { Button } from 'src/chrome'
import {
  UNLIMITED_100_STRIPE_ID,
  UNLIMITED_250_STRIPE_ID,
} from 'src/marketing/plans/constants'
import {
  BuyButton,
  ExpenseReloadForm,
  PaymentMethod,
  PlanContext,
  ShippingAddress,
} from 'src/payments/components'
import { useActions, useMutations, useState } from 'src/hooks'
// relative imports
import styles from '../../payments/containers/styles'
import { Spacer, Text } from '@sendoutcards/quantum-design-ui'

export interface ExpenseReload {
  purchaseAmount: number
  threshold: number
}

type Props = {
  account: AccountFragment
  paymentInfo: PlanPaymentInfo
  onCancel: () => void
  onPurchase: (expenseReload: ExpenseReload | undefined) => void
}

const Payment: React.FC<Props> = props => {
  const { paymentInfo, account, onCancel, onPurchase } = props

  const mutations = useMutations()
  const actions = useActions()

  const buyingUnlimitedPlus = paymentInfo.items?.find(
    item =>
      item.stripeId === UNLIMITED_100_STRIPE_ID ||
      item.stripeId === UNLIMITED_250_STRIPE_ID,
  )

  const [isExpenseReloadEnabled, setIsExpenseReloadEnabled] = useState(false)

  const [expenseReload, setExpenseReload] = useState<ExpenseReload>({
    purchaseAmount: 10,
    threshold: 100,
  })

  const showExpenseReload = buyingUnlimitedPlus || isExpenseReloadEnabled

  const updateAccount = async (
    data: AccountInput,
    success: typeof noop = noop,
    error: typeof noop = noop,
  ) => {
    try {
      const {
        updateAccount: { account },
      } = await mutations.updateAccount({ account: { ...data } })
      actions.updatedAccount(account)
    } catch {
      error()
    }
  }

  return (
    <div style={{ position: 'relative' }}>
      <div css={styles.paymentWrapper} style={{ padding: '0 25px' }}>
        <div css={styles.accountColumn}>
          <ShippingAddress
            account={account}
            isEditable={true}
            title={'Return Address'}
          />

          <div css={styles.sections}>
            <div css={styles.sectionTitle}>
              <Text type="body">Payment Method</Text>
            </div>

            <PaymentMethod
              account={account}
              paymentInfo={paymentInfo}
              updateAccount={updateAccount}
              onError={error => console.error(error)}
              isMixedOrder={false}
              isUsingCredits={false}
              setIsUsingCredits={(value: boolean) => {}}
            />
          </div>
        </div>

        <div css={styles.orderColumn}>
          <PlanContext plans={paymentInfo.items}>
            <>
              <Spacer space="x5" />
              <Text type="caption" inset={{ left: 'x1' }}>
                {expenseReload
                  ? 'Configure your Expense Bucket'
                  : 'Add an Expense Bucket'}
              </Text>
              {!buyingUnlimitedPlus && (
                <Switch
                  checked={isExpenseReloadEnabled}
                  onChange={() => setIsExpenseReloadEnabled(x => !x)}
                />
              )}
              {showExpenseReload && (
                <>
                  <ExpenseReloadForm
                    expenseReload={{
                      ...expenseReload,
                      isEnabled: true,
                      isRequired: false,
                    }}
                    onChange={(name, value) =>
                      setExpenseReload(x => ({ ...x, [name]: value }))
                    }
                  />
                  <Spacer space="x3" />
                  <Text type="footnote" inset="x2">
                    {`*We will charge $${expenseReload.purchaseAmount} of expense to your card today`}
                  </Text>
                </>
              )}
            </>
          </PlanContext>
        </div>

        <div css={styles.paymentAction}>
          <Button onClick={onCancel} style={{ width: 126 }} title="Cancel" />

          <BuyButton
            account={account}
            onPurchase={() =>
              onPurchase(showExpenseReload ? expenseReload : undefined)
            }
            order={undefined}
            purchaseType={paymentInfo.purchaseType}
          />
        </div>
      </div>
    </div>
  )
}

export default Payment
